// Модалите на листа „Доставка" на едно място: запис, проблем с продукт,
// „Проблемни продукти" при запис, „Целият списък", стоковите към фактурата,
// нов продукт и въпросът за незавършената чернова. Състоянието (кой е
// отворен) живее в листа — тук са само рендерът и връзките.
import { useTranslation } from "react-i18next";
import type { Delivery, DeliveryDocumentType, Product } from "../../../../api/inventory";
import {
  CatalogPickerModal,
  type CatalogEntryValues,
  type CatalogItem,
} from "../../components/search/CatalogPickerModal";
import { DeliverySaveModal } from "./DeliverySaveModal";
import { DeliveryProblemModal, DeliveryProblemSaveModal } from "./DeliveryProblemModals";
import { DeliveryNotesPickerModal } from "./DeliveryNotes";
import { ProductCreateModal } from "../../components/product/ProductCreateModal";
import { DraftResumePrompt } from "../../components/drafts/DraftResumePrompt";
import { DRAFT_TEXTS } from "../../components/drafts/draftTexts";
import { summaryLineItem } from "../../components/confirm/confirmPreviews";
import type { useDeliverySave } from "./useDeliverySave";
import type { DeliveryDraft, DeliveryRowDraft } from "./deliveryWorksheetShared";

type Props = {
  mode: "new" | "edit";
  documentType: DeliveryDocumentType;
  date: string;
  invoiceNumber: string;
  // със стоковите на фактурата — плаща се и за тях
  totalGross: number;
  validCount: number;
  // редовете на обобщените стокови (в „N продукта" на модала за запис)
  noteLines: number;
  save: ReturnType<typeof useDeliverySave>;
  nameOf: (productId: number) => string;
  decimalsOf: (productId: number) => number;
  rowOf: (productId: number) => DeliveryRowDraft;
  patchRow: (productId: number, mutate: (row: DeliveryRowDraft) => void) => void;
  problemFor: number | null;
  onProblemClose: () => void;
  catalog: {
    open: boolean;
    items: CatalogItem[];
    priceLabel: string;
    // избраният доставчик — подсказката на чипа „От този доставчик"
    supplierName: string | null;
    onClose: () => void;
    // с въведеното в редовете (количество, ед. цена) — `addFromCatalog`
    onAdd: (ids: number[], values?: CatalogEntryValues) => void;
    // след затваряне без добавяне → фокусът обратно в търсачката
    onAfterClose: (added: boolean) => void;
  };
  notes: {
    open: boolean;
    all: Delivery[];
    selectedIds: number[];
    onClose: () => void;
    onApply: (ids: number[]) => void;
  };
  productModal: { initialName: string } | null;
  onProductClose: () => void;
  onProductCreated: (product: Product) => void;
  // след анимацията на затваряне: фокусът — в новия ред или обратно в търсачката
  onProductAfterClose: (created: boolean) => void;
  // незавършената чернова, намерена при отваряне (`useDraftResume`)
  draftPrompt: DeliveryDraft | null;
  draftSummary: string[];
  onDraftContinue: () => void;
  onDraftDiscard: () => void;
  // Esc/фон — затваря въпроса, без да решава
  onDraftDismiss: () => void;
};

export const DeliveryWorksheetModals = ({
  mode,
  documentType,
  date,
  invoiceNumber,
  totalGross,
  validCount,
  noteLines,
  save,
  nameOf,
  decimalsOf,
  rowOf,
  patchRow,
  problemFor,
  onProblemClose,
  catalog,
  notes,
  productModal,
  onProductClose,
  onProductCreated,
  onProductAfterClose,
  draftPrompt,
  draftSummary,
  onDraftContinue,
  onDraftDiscard,
  onDraftDismiss,
}: Props) => {
  const { t } = useTranslation();
  return (
    <>
      <DeliverySaveModal
        open={save.saveOpen}
        documentType={documentType}
        initial={{ date, documentNumber: invoiceNumber, paid: false, method: "bank" }}
        askPayment={mode === "new"}
        linesCount={(save.excludeProblems ? validCount - save.problemRows.length : validCount) + noteLines}
        totalGross={totalGross}
        saving={save.pending}
        incomingLines={save.incomingLines}
        editMovementId={save.editMovementId}
        onBack={save.closeSave}
        onSave={save.submit}
      />
      <DeliveryProblemModal
        open={problemFor != null}
        productName={problemFor != null ? nameOf(problemFor) : ""}
        current={problemFor != null ? (rowOf(problemFor).problem ?? null) : null}
        onClose={onProblemClose}
        onSave={(problem) => {
          if (problemFor != null)
            patchRow(problemFor, (row) => {
              row.problem = problem;
            });
          onProblemClose();
        }}
      />
      <DeliveryProblemSaveModal
        open={save.probSaveOpen}
        items={save.problemRows.map(([pidStr, row]) => ({
          key: pidStr,
          name: nameOf(+pidStr),
          problem: row.problem ?? "",
        }))}
        restCount={validCount - save.problemRows.length}
        onClose={save.closeProbSave}
        onHold={save.hold}
        onSaveWithout={save.saveWithoutProblems}
      />
      {/* Въвеждане в реда: количество в мярката на листа + ед. цена нето →
          сумата; вече добавеният продукт може да се отметне (не се дублира —
          получава въведеното) */}
      <CatalogPickerModal
        open={catalog.open}
        items={catalog.items}
        priceLabel={catalog.priceLabel}
        entry={{ kind: "qtyPrice", qtyLabel: t("inventoryPage.colQty") }}
        addedSelectable
        supplierName={catalog.supplierName}
        onClose={catalog.onClose}
        onAdd={catalog.onAdd}
        onAfterClose={catalog.onAfterClose}
      />
      <DeliveryNotesPickerModal
        open={notes.open}
        notes={notes.all}
        selectedIds={notes.selectedIds}
        decimalsOf={decimalsOf}
        onClose={notes.onClose}
        onApply={notes.onApply}
      />
      <ProductCreateModal
        open={productModal != null}
        initialName={productModal?.initialName ?? ""}
        onClose={onProductClose}
        onCreated={onProductCreated}
        onAfterClose={onProductAfterClose}
      />
      <DraftResumePrompt
        open={draftPrompt != null}
        texts={DRAFT_TEXTS.delivery}
        savedAt={draftPrompt?.savedAt ?? null}
        savedBy={draftPrompt?.savedBy ?? null}
        items={draftSummary.map(summaryLineItem)}
        onContinue={onDraftContinue}
        onDiscard={onDraftDiscard}
        onDismiss={onDraftDismiss}
      />
    </>
  );
};

export default DeliveryWorksheetModals;
